/**
 * The shape a menu takes while somebody is editing it (SPEC §6.16, §6.19).
 *
 * Deliberately not the generated API types. A meal template and an event menu
 * are different resources with different contracts, and both are edited by the
 * same component — so the editor works in its own vocabulary and each screen
 * translates at the edge. Tying it to one of them would make the other one a
 * fork waiting to happen.
 */

/** How a menu item is filled. */
export type ParticipationMode = 'HostProvided' | 'SignupRequired' | 'Informational'

export interface MenuItemDraft {
  /** Null for an item that does not exist yet. */
  id: string | null
  name: string
  notes: string | null
  participationMode: ParticipationMode
  quantityNeeded: number | null
  unitLabel: string | null
  isFlexible: boolean
  allowOverClaim: boolean
}

export interface MenuSectionDraft {
  id: string | null
  name: string
  items: MenuItemDraft[]
}

/**
 * What each mode means, in the words the product uses.
 *
 * The descriptions are on screen rather than in a tooltip because the
 * difference between "host provided" and "for information" is exactly the sort
 * of thing an organizer gets wrong once and then has to undo in front of
 * everybody.
 */
export const participationModes: ReadonlyArray<{
  value: ParticipationMode
  title: string
  description: string
}> = [
  {
    value: 'SignupRequired',
    title: 'Someone brings it',
    description: 'Families can sign up for this.',
  },
  {
    value: 'HostProvided',
    title: 'Host provides',
    description: 'On the menu so people know what they are coming to. Nobody signs up.',
  },
  {
    value: 'Informational',
    title: 'Just so they know',
    description: 'Context rather than a job. "Coffee after", "bring swimsuits".',
  },
]

export function emptyItem(): MenuItemDraft {
  return {
    id: null,
    name: '',
    notes: null,
    // The mode that shows the item and asks for help, rather than one that
    // quietly hides it from everybody.
    participationMode: 'SignupRequired',
    quantityNeeded: null,
    unitLabel: null,
    isFlexible: false,
    allowOverClaim: false,
  }
}

export function emptySection(): MenuSectionDraft {
  return { id: null, name: '', items: [] }
}

/**
 * Moves one entry of a list, returning a new list.
 *
 * Returns the same array when the move would fall off either end, so a caller
 * can use the identity to decide whether anything happened without comparing
 * contents.
 */
export function moved<T>(list: readonly T[], from: number, offset: number): T[] {
  const to = from + offset

  if (from < 0 || from >= list.length || to < 0 || to >= list.length) {
    return list as T[]
  }

  const next = [...list]
  const [entry] = next.splice(from, 1)
  next.splice(to, 0, entry)

  return next
}
